/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import CarouselNavigation from '@/components/molecules/carousel-navigation/carousel-navigation';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import { SliderImageItem } from '@/components/organisms/image-slider/image-slider';
import ProductCard, {
  ProductCardProps,
} from '@/components/organisms/product-card/product-card';
import CarouselSlideContent from '@/components/utilities/carousel-slide-content/carousel-slide-content';
import Column, {
  ColumnBreakPoints,
} from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useQueryHotelsPictures from '@/integrations/offers/hotels/pictures/pictures.hooks';
import { useFavoritesContext } from '@/libraries/contexts/favorites-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './custom-product-list.module.scss';

export interface CustomProductListProps extends ModuleWrapperProps {
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The text to show as a headline */
  headline?: string;
  /** Whether the module is rendered inside the profile page */
  isInProfilePage?: boolean;
  /** Whether the module is loading */
  isLoading?: boolean;
  /** The list of items to show */
  items?: Content.Components['ProductCardDto'][];
  /** The text to show as a pre headline */
  preHeadline?: string;
  /** The text to show as a sub headline */
  subHeadline?: string;
  /** The text to show as a description */
  text?: string | null;
  /** The variant of the module to use */
  variant?: Content.Components['CustomProductListModuleDto']['variant'];
}

interface InteractedItem {
  hotelId: number | null;
  offerId: string | null;
  pictures: Content.Properties.HotelPicture[];
}

const DEFAULT_IMAGE_NUMBER = 4;
const LIST_LIMIT = 4;
const LOADING_CARDS_COUNT = 4;
const LOADING_GRID_CARDS_COUNT = 3;

const CustomProductList = ({
  ctaLinks = [],
  headline,
  isInProfilePage = false,
  isLoading = false,
  items = [],
  preHeadline,
  subHeadline,
  settings,
  text,
  variant = 'primary',
  ...rest
}: CustomProductListProps) => {
  const currencyFormatter = useCurrencyFormatter();
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [interactedItem, setInteractedItem] = useState<InteractedItem>({
    hotelId: null,
    offerId: null,
    pictures: [],
  });
  const swiperRef = useRef<SwiperInstance>();
  const [interactedItems, setInteractedItems] = useState<InteractedItem[]>([]);
  const { data: dataHotelsPictures } = useQueryHotelsPictures({
    hotelId: interactedItem.hotelId,
  });
  const { favoriteOffers, addFavoriteOffer, removeFavoriteOffer, isFavorite } =
    useFavoritesContext();

  useEffect(() => {
    if (dataHotelsPictures) {
      setInteractedItems(prevHotelsData => {
        return prevHotelsData.map(hotel =>
          hotel.offerId === interactedItem.offerId
            ? ({
                ...hotel,
                pictures: dataHotelsPictures,
              } as unknown as InteractedItem)
            : hotel,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataHotelsPictures]);

  const handleClickFavorite = ({
    isFavorite,
    offerId,
  }: {
    isFavorite: boolean;
    offerId: string;
  }) => {
    if (isFavorite) {
      removeFavoriteOffer(offerId);
    } else {
      addFavoriteOffer(offerId);
    }
  };

  const formattedProductCards = useMemo((): ProductCardProps[] => {
    return items.map(item => {
      const combinedPictures = [
        ...item.pictures, // Keep the original 4 pictures
        ...(
          interactedItems.find(
            interactedItem => interactedItem.offerId === item.uniqueId,
          )?.pictures || []
        ).slice(DEFAULT_IMAGE_NUMBER), // Add new pictures except the first 4
      ];

      const images: SliderImageItem[] = combinedPictures.map(picture => ({
        altText: picture.description || '',
        url: picture.url,
      }));

      const isFavoriteOffer = isFavorite(item.uniqueId);

      return {
        additionalTag: item.additionalTag ?? undefined,
        departureName: item.departureName ?? undefined,
        description: item.description,
        duration: item.daysDuration
          ? `${item.daysDuration} ${t('items.search.productCard.nights')}`
          : undefined,
        highlightScheduled: item.highlightScheduled ?? undefined,
        homeboundDepartureDate: item.homeboundDepartureDate ?? undefined,
        homeboundDepartureName: item.homeboundDepartureName ?? undefined,
        hotelId: item.hotelId,
        hotelName: item.hotelName,
        images,
        imageTags: item.facilityTags,
        includedProducts: item.includedProducts,
        isFavorite: isFavoriteOffer,
        location: item.location ?? undefined,
        middleSection: item.middleSection,
        offerAmount:
          item.discount && item.normalPrice
            ? currencyFormatter(item.normalPrice)
            : '',
        offerId: item.offerId,
        onClickFavorite: () =>
          handleClickFavorite({
            isFavorite: isFavoriteOffer,
            offerId: item.uniqueId as string,
          }),
        onFetchMoreImages: () => {
          const interactedItem = {
            hotelId: item.hotelId,
            offerId: item.uniqueId,
            pictures: [],
          } as InteractedItem;

          setInteractedItem(interactedItem);
          setInteractedItems(previousValues => {
            return [...previousValues, interactedItem];
          });
        },
        outboundArrivalDate: item.outboundArrivalDate ?? undefined,
        outboundDepartureDate: item.outboundDepartureDate ?? undefined,
        outboundDepartureName: item.outboundDepartureName ?? undefined,
        price: item.totalPrice ? currencyFormatter(item.totalPrice) : '',
        priceDetails: item.detailsModal ? item.detailsModal : undefined,
        priceDiscount: item.discount
          ? `${t('items.search.productCard.save')} ${currencyFormatter(item.discount)}`
          : '0',
        priceLabel: item.priceText,
        productCardType: item.productCardType,
        providerCode: item.brandTag ?? undefined,
        rating: item.stars || undefined,
        showHeartHotelLogo: item.isHeartHotel,
        showImageOverlaysOnFirstSlide: item.showImageOverlaysOnFirstSlide,
        specialHotelType: item.specialHotelType,
        tags: item.facilityHighlight,
        target: item.link?.target,
        transportMode: item.transportMode,
        tripHighlights: item.tripHighlights,
        tripSummary: item.tripSummary,
        urgencyTags: item.urgencyTags,
        url: item.url,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteOffers, interactedItems, items]);

  const handleClickShowMore = () => {
    setShowAll(true);
  };

  const componentClassName = classNames(styles.component, {
    [styles.isOffset]: currentIndex !== 0,
    [styles.isPrimary]: variant === 'primary',
    [styles.isProfilePage]: isInProfilePage,
    [styles.isSecondary]: variant === 'secondary',
    [styles.isTertiary]: variant === 'tertiary',
    [styles.isQuaternary]: variant === 'quaternary',
  });

  const textColumnWidth: ColumnBreakPoints =
    variant === 'primary' ? { desktop: 3, laptop: 4 } : { laptop: 6 };
  const itemsColumnWidth: ColumnBreakPoints =
    variant === 'primary' ? { desktop: 9, laptop: 8 } : {};

  if (isLoading) {
    const loadingContent = (
      <Row className={styles.row}>
        <Column className={styles.textColumn} width={textColumnWidth}>
          <SkeletonLoader
            className={classNames(styles.preHeadline, styles.loader)}
            height="16px"
            width="40%"
          />

          <SkeletonLoader
            className={classNames(styles.headline, styles.loader)}
            height="32px"
            width="80%"
          />

          <SkeletonLoader
            className={classNames(styles.subHeadline, styles.loader)}
            height="24px"
            width="60%"
          />

          <div className={styles.loadingText}>
            <SkeletonLoader
              className={styles.loader}
              height="16px"
              width="100%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="16px"
              width="80%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="16px"
              width="70%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="16px"
              width="90%"
            />
          </div>

          <SkeletonLoader
            borderRadius="999px"
            className={styles.loader}
            height="48px"
            width="160px"
          />
        </Column>

        <Column className={styles.itemsColumn} width={itemsColumnWidth}>
          {variant === 'tertiary' ? (
            <div className={styles.products}>
              {Array.from({ length: LOADING_CARDS_COUNT }).map(
                (_, itemIndex) => (
                  <ProductCard key={itemIndex} isLoading variant="primary" />
                ),
              )}
            </div>
          ) : variant === 'quaternary' ? (
            <div className={styles.products}>
              {Array.from({ length: LOADING_GRID_CARDS_COUNT }).map(
                (_, itemIndex) => (
                  <ProductCard
                    key={itemIndex}
                    compact
                    isLoading
                    variant="tertiary"
                  />
                ),
              )}
            </div>
          ) : (
            <div className={styles.swiper}>
              <div className={styles.loadingItems}>
                {Array.from({ length: LOADING_CARDS_COUNT }).map(
                  (_, itemIndex) => (
                    <div key={itemIndex} className={styles.item}>
                      <div className={styles.itemInner}>
                        <ProductCard isLoading variant="tertiary" />
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </Column>
      </Row>
    );

    if (isInProfilePage) {
      return <div className={componentClassName}>{loadingContent}</div>;
    }

    return (
      <ModuleWrapper
        {...rest}
        allowOverflow
        className={componentClassName}
        settings={settings}
      >
        <Container>{loadingContent}</Container>
      </ModuleWrapper>
    );
  }

  const content = (
    <Row className={styles.row}>
      <Column className={styles.textColumn} width={textColumnWidth}>
        {preHeadline && (
          <Typography
            className={styles.preHeadline}
            tag="p"
            tagStyle="subHeading"
          >
            {preHeadline}
          </Typography>
        )}

        {headline && (
          <Typography
            className={styles.headline}
            tag="h2"
            tagStyle="displayMedium"
          >
            {headline}
          </Typography>
        )}

        {subHeadline && (
          <Typography
            className={styles.subHeadline}
            tag="p"
            tagStyle="bodyLargeBold"
          >
            {subHeadline}
          </Typography>
        )}

        {text && <RichText text={text} />}

        {ctaLinks.length > 0 && <CtaLinkList items={ctaLinks} />}
      </Column>

      <Column className={styles.itemsColumn} width={itemsColumnWidth}>
        {variant === 'tertiary' ? (
          <React.Fragment>
            <div className={styles.products}>
              {formattedProductCards
                .slice(0, showAll ? items.length : LIST_LIMIT)
                .map((item, itemIndex) => (
                  <ProductCard
                    key={itemIndex}
                    theme={settings?.theme}
                    {...item}
                    variant="primary"
                  />
                ))}
            </div>

            {!showAll && LIST_LIMIT < items.length && (
              <div className={styles.buttonContainer}>
                <Button
                  onClick={handleClickShowMore}
                  text={t('items.search.results.showMore', {
                    number: items.length - LIST_LIMIT,
                  })}
                  variant="secondary"
                />
              </div>
            )}
          </React.Fragment>
        ) : variant === 'quaternary' ? (
          <div className={styles.products}>
            {formattedProductCards.map((item, itemIndex) => (
              <ProductCard
                key={itemIndex}
                {...item}
                compact
                theme={settings?.theme}
                variant="tertiary"
              />
            ))}
          </div>
        ) : (
          <Swiper
            key={`swiper-${isLaptop}`}
            breakpoints={{
              [parseInt(breakpoints.tablet)]: {
                slidesPerView: 2,
                spaceBetween: 32,
                speed: 500,
              },
              [parseInt(breakpoints.desktop)]: {
                slidesPerView: isInProfilePage
                  ? 3
                  : variant === 'primary'
                    ? 3
                    : 4,
                spaceBetween: 32,
                speed: 750,
              },
            }}
            className={styles.swiper}
            onSlideChangeTransitionEnd={item => {
              setCurrentIndex(item.snapIndex);
            }}
            onSlideChangeTransitionStart={item => {
              setCurrentIndex(item.snapIndex);
            }}
            onSwiper={swiper => {
              swiperRef.current = swiper;
            }}
            slidesPerGroup={1}
            slidesPerView={1}
            spaceBetween={0}
            speed={500}
            // Tracks which slides are in view, for CarouselSlideContent
            watchSlidesProgress
          >
            {formattedProductCards.map((item, itemIndex) => (
              <SwiperSlide
                key={itemIndex}
                className={classNames(styles.item, {
                  [styles.isActive]: itemIndex === currentIndex,
                  [styles.hasProviderCode]: Boolean(item.providerCode),
                })}
              >
                <CarouselSlideContent>
                  <div className={styles.itemInner}>
                    <ProductCard
                      {...item}
                      compact
                      theme={settings?.theme}
                      variant="tertiary"
                    />
                  </div>
                </CarouselSlideContent>
              </SwiperSlide>
            ))}

            <CarouselNavigation
              ariaLabel={headline || undefined}
              showPageCount
              swiperRef={swiperRef}
            />
          </Swiper>
        )}
      </Column>
    </Row>
  );

  if (isInProfilePage) {
    return <div className={componentClassName}>{content}</div>;
  }

  return (
    <ModuleWrapper
      {...rest}
      allowOverflow
      className={componentClassName}
      settings={settings}
    >
      <Container>{content}</Container>
    </ModuleWrapper>
  );
};

CustomProductList.displayName = 'CustomProductList';

export default CustomProductList;
